import { useSetModal } from "@/shared/atoms/useModal";
import useCategoryHook from "@/shared/hooks/useCategoryHook";
import useCommon from "@/shared/hooks/useCommon";
import useDebounce from "@/shared/hooks/useDebounce";
import useMenuItemHook from "@/shared/hooks/useMenuItemHook";
import { MenuItems } from "@/shared/utils/enum";
import {
  formatPrice,
  guidGenerator,
  promiseAllSequential,
} from "@/shared/utils/helpers";
import { Category as ICategory, Topping } from "@/shared/utils/interfaces";
import { atom, useAtom } from "jotai";
import { MouseEventHandler, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import Button from "../Button/Button";
import Checkbox from "../Checkbox/Checkbox";
import Collapse from "../Collapse/Collapse";
import Search from "../Input/Search";
import Textfield from "../Input/Textfield";
import Modal, { ModalProps } from "../Modal/Modal";
import Spinner from "../Spinner/Spinner";
import { toggle } from "../Table/helpers";

export interface CopyToppingsModalProps extends ModalProps {}

const searchAtom = atom("");

const Category = ({
  category,
  setSelected,
  selected,
}: {
  category: ICategory;
  selected: number[];
  setSelected: Function;
}) => {
  const [active, setActive] = useState(false);
  const isActive = category.sub_content.every((p) =>
    selected.includes(p.item_id),
  );

  const onClick: MouseEventHandler<HTMLDivElement> = (e) => {
    e.stopPropagation();
    setSelected((current) =>
      isActive
        ? current.filter(
            (id) => !category.sub_content.some((p) => p.item_id === id),
          )
        : [...current, ...category.sub_content.map((p) => p.item_id)],
    );
  };
  return (
    <div className="cursor-pointer">
      <div
        className="mb-4 flex items-center gap-4 bg-primary-dark p-4 text-[#ffffff]"
        onClick={() => setActive(!active)}
        key={category.item_id}
      >
        <div className="mr-[1rem]">
          <Checkbox onClick={onClick} active={isActive} />
        </div>
        {category.name?.all}
      </div>
      <Collapse active={active}>
        {category.sub_content.map((product) => (
          <div
            className="mb-4 flex items-center gap-8 px-4 py-1"
            onClick={() =>
              setSelected((current) => toggle(current, product.item_id))
            }
            key={product.item_id}
          >
            <div>
              <Checkbox active={selected.includes(product.item_id)} />
            </div>
            <div className="w-[30rem]">{product.name?.all}</div>
            <div style={{ fontWeight: "900" }}>
              {formatPrice(product.price)}
            </div>
          </div>
        ))}
      </Collapse>
    </div>
  );
};

const Edit = ({ setEdit, selected }) => {
  const [existing, setExcisting] = useState(false);
  const setModal = useSetModal();
  const { toppingCreate } = useCategoryHook();

  const { register, errors, handleSubmit } = useForm({ defaultValues: [] });
  const { data: products } = useMenuItemHook<Topping[]>(MenuItems.PRODUCT);
  const filteredProducts = useMemo(
    () => products?.filter((p) => selected.includes(p.item_id)),
    [products, selected],
  );
  const t = useCommon();

  const onSubmit = (data) => {
    const records = data.data.map((record, i) => {
      const product = filteredProducts[i];
      const req = {
        ...record,
        vat_enum: product.vat_enum,
        image_cloudinary_id: product?.image_cloudinary_id,
        code: guidGenerator(),
      };
      return () =>
        toppingCreate.mutateAsync({
          ...req,
          item_type: MenuItems.TOPPING,
        });
    });
    return promiseAllSequential(records).then(() => {
      setModal("");
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="mt-[1rem]">
      {!filteredProducts ? (
        <div className="flex min-h-100px items-center justify-center">
          <Spinner />
        </div>
      ) : (
        filteredProducts?.map((topping, index) => (
          <div className="mb-4 flex items-center gap-4" key={topping.item_id}>
            <Textfield
              defaultValue={topping?.name?.all}
              name={`data.[${index}].name`}
              error={errors?.[`data.[${index}].name`]?.message}
              ref={register({
                required: true,
              })}
              placeholder={t("toppings.name")}
            />
            <Textfield
              name={`data.[${index}].description`}
              error={errors?.[`data.[${index}].description`]?.message}
              ref={register({})}
              placeholder={t("toppings.description")}
            />
            <Textfield
              defaultValue={topping.price}
              type="number"
              step=".01"
              min="0"
              name={`data.[${index}].price`}
              error={errors?.[`data.[${index}].price`]?.message}
              ref={register({})}
              placeholder={t("toppings.price")}
            />
          </div>
        ))
      )}
      <div className="flex gap-4">
        <Button type="button" onClick={() => setEdit(false)}>
          {t("back")}
        </Button>
        <Button isPending={toppingCreate.isPending}>{t("save")}</Button>
      </div>
    </form>
  );
};

const Filter = ({ edit, setEdit, selected }) => {
  const [search, setSearch] = useAtom(searchAtom);
  const t = useCommon();

  return (
    <div className="flex gap-4">
      <Search
        placeholder={t("filter")}
        onDeleteClick={() => setSearch("")}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <Button
        onClick={() => {
          setEdit(!edit);
          setSearch("");
        }}
        isDisabled={selected.length === 0}
      >
        {t(!edit ? "next" : "previous")}
      </Button>
    </div>
  );
};

const Select = ({ selected, setSelected }) => {
  const [search] = useAtom(searchAtom);
  const debounced = useDebounce(search, 500);
  const { query, categories } = useCategoryHook();

  useEffect(() => {
    query.refetch();
  }, []);

  const filtered = useMemo(
    () =>
      categories?.filter(
        (category) =>
          category?.name?.all
            ?.toLowerCase()
            .includes(debounced.toLowerCase()) ||
          category.sub_content.some(
            (product) =>
              product.name?.all &&
              product?.name?.all
                ?.toLowerCase()
                .includes(debounced.toLowerCase()),
          ),
      ),
    [categories, debounced],
  );

  return (
    <div className="mt-[1rem]">
      {query.isPending ? (
        <Spinner />
      ) : (
        filtered?.map((category) => (
          <Category
            selected={selected}
            setSelected={setSelected}
            category={category}
            key={category.item_id}
          />
        ))
      )}
    </div>
  );
};

const CopyToppingsModal = ({ ...props }: CopyToppingsModalProps) => {
  const [edit, setEdit] = useState(false);
  const [selected, setSelected] = useState<number[]>([]);
  const t = useCommon();

  return (
    <Modal {...props}>
      <h2 className="mt-[0]">{t("toppings.copyExtrasFromProducts")}</h2>
      {!edit && <Filter selected={selected} edit={edit} setEdit={setEdit} />}
      {!edit ? (
        <Select selected={selected} setSelected={setSelected} />
      ) : (
        <Edit setEdit={setEdit} selected={selected} />
      )}
    </Modal>
  );
};

export default CopyToppingsModal;
